import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Chip from '@ntuctech/devex-tangram/Chip';
import Typography from '@ntuctech/devex-tangram/Typography';
import Grid from '@ntuctech/devex-tangram/Grid';
import { SCREEN_SIZE } from '../../lib/Media';
import { GTM_ENV_VAR as GEV, PAGE_TYPES, SCREEN_NAME } from '../../lib/eventTrackerFunction';
import PlayButtonIMG from '../../static/playbutton.svg';
import { isPastDate } from '../../utils/Utils';
import { AMP_EVENT_NAME } from '../../lib/amplitude';

const EVENT_PROPERTY = 'Event Property';
const EGIFTING_HOME = 'egifting | home';

const MainContainer = styled.div`
  background: ${(p) => p.theme.ColorBaseMineShaft40};
`;

const Container = styled.div`
  max-width: 59rem;
  padding: 4rem 0 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0px auto;
`;

const ContentTag = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 100%;
  overflow: auto;
  position: sticky;
  top: 0;
  margin: 1.5rem 0;
  background: ${(p) => p.theme.ColorBaseMineShaft40};
  width: 100%;
  justify-content: flex-start;
  ${SCREEN_SIZE.From.Tablet} {
    justify-content: center;
  }
  ${SCREEN_SIZE.Below.MobileL} {
    padding: 0 0.25rem;
  }
`;

const Category = styled(Chip)`
  margin: 0 0.25rem;
  text-transform: capitalize;
  white-space: nowrap;
`;

const GridItem = styled(Grid).attrs({ s: 3, xs: 6, item: true })`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem !important;
`;

const CardImg = styled.img`
  width: 100%;
  object-fit: contain;
  border-radius: 8px;
  cursor: pointer;
  display: block;
  ${SCREEN_SIZE.Only.Mobile} {
    min-height: 7.125rem;
  }
  ${SCREEN_SIZE.From.Tablet} {
    min-height: 9rem;
  }
`;
const HoverBox = styled.div`
  cursor: pointer;
  display: none;
`;
const ImgWrapper = styled.div`
  cursor: pointer;
  :hover {
    border-radius: 8px;
    position: relative;
    top: 3px;
    img {
      display: block;
      border-radius: 11px;
      top: -3px;
      position: relative;
    }
    > div {
      display: block;
      border: 3px solid #fff;
      border-radius: 8px;
      position: absolute;
      top: -2px;
      width: calc(100% - 2px);
      height: calc(100% - 2px);
      z-index: 9;
      left: 1px;
      box-shadow: 0 0 0 1px #1454b8;
    }
  }
`;

const PlayButton = styled.div`
  img {
    width: 3rem !important;
    height: 3rem !important;
    position: absolute;
    left: 40%;
    top: 35%;
  }
`;

const GIFCard = styled.div`
  position: relative !important;
  img {
    width: 100%;
    object-fit: contain;
    border-radius: 8px;
    cursor: pointer;
    display: block;
  }
  ${SCREEN_SIZE.Only.Mobile} {
    min-height: 7.125rem;
  }
  ${SCREEN_SIZE.From.Tablet} {
    min-height: 9rem;
  }
`;

const GiftVoucher = ({ data = [], onClickVoucher, track, isB2bEnabled }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [gifData, setGifData] = useState({});
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 600);

  React.useEffect(() => {
    const updateWindowDimensions = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', updateWindowDimensions);

    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, []);

  // Filter vouchers
  // by selected category
  const cardsData = useMemo(
    () =>
      selectedCategory === 'all'
        ? data.filter((item) => item.type !== 'banner')
        : data.filter((item) => item.catergory === selectedCategory && item.type !== 'banner'),
    [selectedCategory, data]
  );

  const getFileExtension = (filename) => {
    return filename.split('.').pop();
  };

  const grouping = data.filter(
    (item) => !isPastDate(item?.end, true) && isPastDate(item?.start, true)
  );
  const isEnableCategories = grouping.filter(
    (tag, index, array) => array.findIndex((item) => item.catergory === tag.catergory) === index
  );
  isEnableCategories.unshift({
    catergory: 'all',
    title: 'All',
    start: '1970-01-01',
  });

  const tracking = (title) => {
    track({
      [GEV.PAGE_TYPE]: PAGE_TYPES.HOME,
      [GEV.SCREEN_NAME]: SCREEN_NAME.HOME,
      [GEV.EVENT_CAT]: 'Egifting',
      [GEV.EVENT_ACTION]: 'click',
      [GEV.EVENT_LABEL]: 'gifting_startorder_category',
    });
    track({
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.CATEGORY_CHIP_CLICK,
      [GEV.EVENT_CAT]: 'Egifting',
      [GEV.AMPLITUDE_PROPERTY_TYPE]: EVENT_PROPERTY,
      [GEV.AMPLITUDE_VERTICAL]: AMP_EVENT_NAME.VERTICAL,
      [GEV.AMPLITUDE_CTA_LOCATION]: EGIFTING_HOME,
      [GEV.AMPLITUDE_CTA_SUB_LOCATION]: ``,
      [GEV.AMPLITUDE_GIFTING_CATEGORY]: title,
    });
  };

  return (
    <>
      <MainContainer>
        <Container>
          <Typography variant="titleXl">Pick a design</Typography>
          <ContentTag data-testid="tags">
            {isEnableCategories.map((category) => (
              <Category
                data-testid={`voucher-category-${category.catergory}`}
                label={category.title}
                key={category}
                selected={category.catergory === selectedCategory}
                onClick={() => {
                  setSelectedCategory(category.catergory);
                  tracking(category.title);
                }}
              />
            ))}
          </ContentTag>
          <Grid container compact>
            {cardsData.map((option, index) => {
              const isSelected = gifData.isPlaying && index === gifData.index;
              const desktopSrc = isSelected ? cardsData[gifData.index].png : option.image;
              const isExpired = isPastDate(option?.end, true);
              const isStarted = isPastDate(option?.start, true);
              return (
                <>
                  {!isExpired && isStarted ? (
                    <>
                      {getFileExtension(option.png) === 'gif' ? (
                        <GridItem
                          onMouseOver={() =>
                            setGifData((prevState) => ({
                              index,
                              isPlaying: !prevState.isPlaying,
                            }))
                          }
                          onMouseOut={() =>
                            setGifData((prevState) => ({
                              index,
                              isPlaying: !prevState.isPlaying,
                            }))
                          }
                          onClick={() => {
                            onClickVoucher(option);
                          }}
                        >
                          <GIFCard data-testid={`play-button-${index}`}>
                            <CardImg src={isMobile ? option.png : desktopSrc} alt={option.name} />
                            {isSelected || isMobile ? (
                              ''
                            ) : (
                              <PlayButton>
                                <img src={PlayButtonIMG.src} alt="test" />
                              </PlayButton>
                            )}
                          </GIFCard>
                        </GridItem>
                      ) : (
                        <GridItem
                          key={option.id}
                          onClick={() => {
                            track({
                              [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.PAGE_TILE,
                              [GEV.EVENT_CAT]: 'Egifting',
                              [GEV.AMPLITUDE_PROPERTY_TYPE]: EVENT_PROPERTY,
                              [GEV.MEMBER_TYPE]: isB2bEnabled ? 'B2B' : 'B2C',
                              [GEV.AMPLITUDE_VERTICAL]: AMP_EVENT_NAME.VERTICAL,
                              [GEV.AMPLITUDE_CTA_LOCATION]: EGIFTING_HOME,
                              [GEV.AMPLITUDE_CTA_SUB_LOCATION]: `widget | card design tile | ${option.image}`,
                            });
                            onClickVoucher(option);
                          }}
                          data-testid="voucher-image"
                        >
                          <ImgWrapper>
                            <HoverBox />
                            <CardImg src={option.image} alt={option.name} />
                          </ImgWrapper>
                        </GridItem>
                      )}
                    </>
                  ) : null}
                </>
              );
            })}
          </Grid>
        </Container>
      </MainContainer>
    </>
  );
};

export default GiftVoucher;
